// @ts-strict-ignore
import React, { ReactNode } from 'react';
import styled, { ThemeProvider, css } from 'styled-components';

import { Box } from 'components/Box';
import PrintHidden from 'components/PrintHidden';
import AcceptCookiesContainer from 'containers/AcceptCookiesContainer';
import { ClientOnly } from 'containers/ClientOnly';
import FixedSpacerContainer from 'containers/FixedSpacerContainer';
import FlashMessageContainer from 'containers/FlashMessageContainer';
import ModalRoot from 'containers/ModalRoot';
import { NoAmp } from 'containers/NoAmp';
import theme, {
  breakpoints,
  colors,
  fontSizesByType,
  fontWeights,
  fonts,
  gutterWidth,
  maxWidth,
  utilityClasses,
} from 'theme';
import { isDefined } from 'utils/functional';
import { getItem } from 'utils/localStorage';

const testMode = getItem('testMode');
const Root = styled(Box)`
  font-family: ${fonts.sans};
  color: ${colors.greyishBrown};
  * {
    box-sizing: border-box;
    font-family: ${fonts.sans};
  }
  svg {
    box-sizing: content-box;
    pointer-events: none;
  }
  strong {
    font-weight: ${fontWeights.semiBold};
  }
  @page {
    margin-bottom: 0;
    size: auto;
    box-decoration-break: clone;
  }
  ${utilityClasses.map(
    (c) => css`
      .idlst-${c.name.replace(/[aeiou-]/g, '')} {
        ${c.styles}
      }
    `
  )}
`;
const PageContent = styled(Box)`
  ${(props) =>
    props.$centered &&
    `
    width: 100%;
    max-width: ${maxWidth[2]}px;
    padding: 0 ${gutterWidth / 2}px;
    margin: 0 auto;
    @media (min-width: ${breakpoints[2]}) {
      padding: 0 ${gutterWidth}px;
    }
  `}
`;
const buttonBarHeight = '67px';
const ButtonBarContainer = styled.div`
  display: none;
  height: ${buttonBarHeight};
  @media (max-width: ${breakpoints[2]}) {
    display: block;
  }
`;
const ButtonBar = styled(Box)`
  margin-bottom: 0;
  position: fixed;
  height: ${buttonBarHeight};
  width: 100%;
  left: 0;
  bottom: 0;
  background: white;
  box-shadow: 0 -3px 4px 1px rgba(0, 0, 0, 0.15);
  & > button,
  & > a {
    flex-grow: 1;
    &:first-child {
      margin-left: 16px;
    }
    margin-right: 16px;
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const BaseLayout = ({
  children,
  header,
  buttonBar,
  centered,
  stickyHeaderHeight,
}: {
  children: ReactNode;
  header?: ReactNode;
  buttonBar?: ReactNode;
  centered?: boolean;
  stickyHeaderHeight?:
    | (number | null | undefined)
    | Array<number | null | undefined>;
}) => (
  <ThemeProvider theme={theme}>
    <Root
      bg={centered && colors.fog}
      fontSize={fontSizesByType.body}
      id="layout-root"
    >
      <Box classes={['flex']} minHeight="100vh" flexDirection="column">
        {header}
        <ClientOnly>
          <Box
            data-qa-id="some-element"
            position={
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line no-nested-ternary
              testMode || !isDefined(stickyHeaderHeight)
                ? 'static'
                : typeof stickyHeaderHeight === 'number'
                ? 'sticky'
                : stickyHeaderHeight.map((height) =>
                    isDefined(height) ? 'sticky' : 'static'
                  )
            }
            top={stickyHeaderHeight}
            zIndex={3}
          >
            <FlashMessageContainer />
          </Box>
        </ClientOnly>
        <PageContent
          classes={['flex']}
          flexDirection="column"
          flex="1 1 auto"
          $centered={centered}
        >
          {children}
        </PageContent>
      </Box>

      {buttonBar && (
        <NoAmp>
          <PrintHidden>
            <ButtonBarContainer>
              <ButtonBar classes={['flex', 'justify-center', 'align-center']}>
                {buttonBar}
              </ButtonBar>
            </ButtonBarContainer>
          </PrintHidden>
        </NoAmp>
      )}

      <FixedSpacerContainer
        bottom={0}
        left={0}
        zIndex={80}
        transform="translateZ(0)"
        width="100%"
      >
        <ClientOnly>
          <AcceptCookiesContainer />
        </ClientOnly>
      </FixedSpacerContainer>
      <ModalRoot />
    </Root>
  </ThemeProvider>
);

// eslint-disable-next-line import/no-default-export
export default BaseLayout;
